import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth/auth';
import { AdminAnalyticsService, AnalyticsSummary, AnalyticsVisit } from '../../../services/api/admin-analytics';

type Period = 'today' | '7d' | '30d';

interface Share { label: string; value: number; hint?: string }
interface Bar { label: string; value: number }

/** Известные страницы сайта: человекочитаемое название рядом с путём. Остальные показываются только путём. */
const PAGE_TITLES: Record<string, string> = {
  '/': 'Главная',
  '/calculations/new': 'Новый расчёт',
  '/tnved': 'Справочник ТН ВЭД',
  '/login': 'Вход',
  '/contacts': 'Контакты',
  '/info/cargo-owner': 'Грузовладельцам',
  '/info/freight-forwarder': 'Экспедиторам',
  '/info/customs-broker': 'Сопровождение ВЭД',
  '/info/supplier': 'Поставщикам',
  '/registration/cargo-owner': 'Регистрация грузовладельца',
  '/registration/public': 'Регистрация',
  '/user-agreement': 'Пользовательское соглашение',
  '/privacy-policy': 'Политика конфиденциальности',
  '/invite/supplier/:token': 'Приглашение поставщика'
};

const SOURCE_HINTS: Record<string, string> = {
  'Прямые заходы': 'Набрали адрес или из закладок',
  'Google': 'Поиск',
  'Яндекс': 'Поиск',
  'WhatsApp': 'Ссылки из переписки',
  'Другие сайты': 'Переходы с других сайтов'
};

const DEVICE_LABELS: Record<string, string> = { mobile: 'Мобильный', desktop: 'Десктоп', tablet: 'Планшет' };
const LANGUAGE_LABELS: Record<string, string> = { ru: 'Русский', kk: 'Қазақша', en: 'English', zh: '中文' };

const ASTANA_OFFSET_MS = 5 * 60 * 60 * 1000;
const VISITS_PAGE_SIZE = 20;

/**
 * Статистика посетителей: данные приходят с бэкенда (GET /api/admin/analytics/summary и /visits).
 * Периоды считаются по времени Астаны (UTC+5), как и на бэкенде.
 */
@Component({
  selector: 'app-visitor-stats',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './visitor-stats.component.html',
  styleUrl: './visitor-stats.component.scss'
})
export class VisitorStatsComponent implements OnInit {
  period: Period = '30d';

  summary: AnalyticsSummary | null = null;
  loading = false;
  error = false;

  visits: AnalyticsVisit[] = [];
  visitsTotal = 0;
  visitsPage = 0;
  visitsHasNext = false;
  visitsLoading = false;
  visitsError = false;

  hover: { x: number; label: string; value: number } | null = null;

  readonly periods: { key: Period; label: string }[] = [
    { key: 'today', label: 'Сегодня' },
    { key: '7d', label: '7 дней' },
    { key: '30d', label: '30 дней' }
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private api: AdminAnalyticsService
  ) {}

  /** Страница только для администратора, как и список пользователей. */
  ngOnInit(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }
    if (this.authService.getUser()?.role?.code !== 'ROLE_ADMIN') {
      this.router.navigate(['/']);
      return;
    }
    this.loadSummary();
    this.loadVisits(true);
  }

  setPeriod(p: Period): void {
    if (p === this.period) {
      return;
    }
    this.period = p;
    this.hover = null;
    this.loadSummary();
  }

  retry(): void {
    this.loadSummary();
    this.loadVisits(true);
  }

  // ── Загрузка ─────────────────────────────────────────────────────────────────

  private loadSummary(): void {
    const { from, to } = this.range();
    this.loading = true;
    this.error = false;
    this.api.getSummary(from, to).subscribe({
      next: s => {
        this.summary = s;
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  loadVisits(reset = false): void {
    if (this.visitsLoading) {
      return;
    }
    const page = reset ? 0 : this.visitsPage + 1;
    this.visitsLoading = true;
    this.visitsError = false;
    this.api.getVisits(page, VISITS_PAGE_SIZE).subscribe({
      next: r => {
        this.visits = reset ? r.content : [...this.visits, ...r.content];
        this.visitsTotal = r.totalElements;
        this.visitsPage = r.currentPage;
        this.visitsHasNext = r.hasNext;
        this.visitsLoading = false;
      },
      error: () => {
        this.visitsError = true;
        this.visitsLoading = false;
      }
    });
  }

  /** Период в днях по Астане (UTC+5), включительно: «сегодня» — from = to. */
  private range(): { from: string; to: string } {
    const today = new Date(Date.now() + ASTANA_OFFSET_MS);
    const days = this.period === 'today' ? 1 : this.period === '7d' ? 7 : 30;
    const start = new Date(today.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
    return { from: this.isoDate(start), to: this.isoDate(today) };
  }

  /** Дата в формате yyyy-MM-dd; сдвинутый на +5 часов момент читаем через UTC-геттеры. */
  private isoDate(d: Date): string {
    return d.toISOString().slice(0, 10);
  }

  // ── Показатели ───────────────────────────────────────────────────────────────

  get hasData(): boolean {
    return !!this.summary && (this.summary.kpi.pageviews > 0);
  }

  get kpis(): { label: string; value: string; change: number | null; positive: boolean; unit: string; hint: string }[] {
    const k = this.summary?.kpi;
    if (!k) {
      return [];
    }
    const p = k.prev;
    return [
      { label: 'Посетители', value: this.fmt(k.visitors), change: this.pctChange(k.visitors, p.visitors), positive: k.visitors >= p.visitors, unit: '%', hint: 'Уникальные посетители за период' },
      { label: 'Визиты', value: this.fmt(k.visits), change: this.pctChange(k.visits, p.visits), positive: k.visits >= p.visits, unit: '%', hint: 'Сессии: серия просмотров с перерывами меньше 30 минут' },
      { label: 'Просмотры страниц', value: this.fmt(k.pageviews), change: this.pctChange(k.pageviews, p.pageviews), positive: k.pageviews >= p.pageviews, unit: '%', hint: 'Все открытия страниц' },
      { label: 'Время на сайте', value: k.visits ? this.duration(k.avgDurationSec) : '—', change: this.pctChange(k.avgDurationSec, p.avgDurationSec), positive: k.avgDurationSec >= p.avgDurationSec, unit: '%', hint: 'Средняя длительность визита' },
      // Для отказов изменение показываем в процентных пунктах, а рост — это плохо
      { label: 'Отказы', value: k.visits ? `${Math.round(k.bounceRate * 100)}%` : '—', change: this.bounceDelta(k.bounceRate, p.bounceRate), positive: k.bounceRate <= p.bounceRate, unit: ' п.п.', hint: 'Доля визитов, где человек открыл одну страницу и ушёл меньше чем за 15 секунд. Чем ниже, тем лучше' },
      { label: 'Регистрации', value: this.fmt(k.registrations), change: this.pctChange(k.registrations, p.registrations), positive: k.registrations >= p.registrations, unit: '%', hint: 'Новые аккаунты за период' }
    ];
  }

  /** Изменение к прошлому периоду, %; если раньше было 0 — сравнивать не с чем. */
  private pctChange(cur: number, prev: number): number | null {
    if (!prev) {
      return null;
    }
    return Math.round(((cur - prev) / prev) * 100);
  }

  private bounceDelta(cur: number, prev: number): number | null {
    // У прошлого периода не было визитов — отказы 0 по умолчанию, сравнивать нечего
    if (!this.summary || !this.summary.kpi.prev.visits) {
      return null;
    }
    return Math.round((cur - prev) * 100);
  }

  // ── График ───────────────────────────────────────────────────────────────────

  get chart(): Bar[] {
    const s = this.summary?.series;
    if (!s) {
      return [];
    }
    if (s.unit === 'hour') {
      return s.points.map(p => ({ label: `${p.t}:00`, value: p.visitors }));
    }
    return s.points.map(p => ({ label: `${p.t.slice(8, 10)}.${p.t.slice(5, 7)}`, value: p.visitors }));
  }

  get chartTitle(): string {
    return this.summary?.series.unit === 'hour' ? 'Посетители по часам' : 'Посетители по дням';
  }

  get chartMax(): number {
    const max = Math.max(1, ...this.chart.map(b => b.value));
    const step = max > 100 ? 50 : max > 20 ? 10 : max > 4 ? 2 : 1;
    return Math.ceil(max / step) * step;
  }

  // ── Списки ───────────────────────────────────────────────────────────────────

  get pages(): { path: string; title: string; views: number; visitors: number; avgTime: string }[] {
    return (this.summary?.pages ?? []).map(p => ({
      path: p.path,
      title: this.pageTitle(p.path),
      views: p.views,
      visitors: p.visitors,
      avgTime: p.avgDurationSec > 0 ? this.duration(p.avgDurationSec) : '—'
    }));
  }

  private pageTitle(path: string): string {
    if (PAGE_TITLES[path]) {
      return PAGE_TITLES[path];
    }
    if (/^\/calculations\/\d+\/process$/.test(path)) return 'Обработка расчёта';
    if (/^\/calculations\/\d+$/.test(path)) return 'Расчёт';
    if (/^\/public\/calculations\/\d+$/.test(path)) return 'Расчёт';
    return '';
  }

  get cities(): Share[] {
    return (this.summary?.cities ?? []).map(c => ({ label: c.city, value: c.visitors, hint: c.country ?? undefined }));
  }

  get sources(): Share[] {
    return (this.summary?.sources ?? []).map(s => ({ label: s.source, value: s.visitors, hint: SOURCE_HINTS[s.source] }));
  }

  get campaigns(): Share[] {
    return (this.summary?.campaigns ?? []).map(c => ({
      label: [c.utmSource, c.utmCampaign].filter(Boolean).join(' / ') || 'Без метки',
      value: c.visitors
    }));
  }

  get devices(): Share[] {
    return (this.summary?.devices ?? []).map(d => ({ label: DEVICE_LABELS[d.type] ?? d.type, value: d.visitors }));
  }

  get browsers(): Share[] {
    return (this.summary?.browsers ?? []).map(b => ({ label: b.name, value: b.visitors }));
  }

  get languages(): Share[] {
    return (this.summary?.languages ?? []).map(l => ({ label: LANGUAGE_LABELS[l.code] ?? l.code, value: l.visitors }));
  }

  // ── Визиты ───────────────────────────────────────────────────────────────────

  /** Время визита по Астане: смещение уже указано в строке, поэтому берём часы и минуты без пересчёта. */
  visitTime(v: AnalyticsVisit): string {
    const day = `${v.startedAt.slice(8, 10)}.${v.startedAt.slice(5, 7)}`;
    return `${day} ${v.startedAt.slice(11, 16)}`;
  }

  visitDevice(v: AnalyticsVisit): string {
    const type = DEVICE_LABELS[v.deviceType] ?? v.deviceType;
    return v.browser ? `${type} · ${v.browser}` : type;
  }

  visitDuration(v: AnalyticsVisit): string {
    return v.durationSec == null ? '—' : this.duration(v.durationSec);
  }

  // ── Форматирование ───────────────────────────────────────────────────────────

  total(list: Share[]): number {
    return list.reduce((s, x) => s + x.value, 0);
  }

  pct(value: number, list: Share[]): number {
    const t = this.total(list);
    return t ? Math.round((value / t) * 1000) / 10 : 0;
  }

  maxOf(list: Share[]): number {
    return Math.max(1, ...list.map(x => x.value));
  }

  fmt(n: number): string {
    return Math.round(n).toLocaleString('ru-RU');
  }

  /** Секунды → м:сс. */
  duration(sec: number): string {
    const s = Math.max(0, Math.round(sec));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  // ── Геометрия графика (SVG 720×220, слева место под подписи оси) ──
  readonly W = 720;
  readonly H = 220;
  readonly padL = 36;
  readonly padB = 24;

  get barStep(): number {
    return (this.W - this.padL) / Math.max(1, this.chart.length);
  }

  barX(i: number): number {
    return this.padL + i * this.barStep + this.barStep * 0.18;
  }

  get barW(): number {
    return this.barStep * 0.64;
  }

  barH(v: number): number {
    return ((this.H - this.padB) * v) / this.chartMax;
  }

  get gridLines(): number[] {
    return [0, 0.5, 1].map(f => Math.round(this.chartMax * f));
  }

  /** Подписи по оси X — не каждая, чтобы не слипались. */
  showLabel(i: number): boolean {
    const n = this.chart.length;
    const every = n > 24 ? 5 : n > 12 ? 3 : 1;
    return i % every === 0 || i === n - 1;
  }

  /** Столбец с закруглённой верхушкой (4px) и плоским основанием на оси. */
  barPath(i: number, v: number): string {
    const x = this.barX(i);
    const w = this.barW;
    const h = this.barH(v);
    const base = this.H - this.padB;
    if (h <= 0) {
      // Нулевой день: тонкая риска на оси, чтобы было видно, что период учтён
      return `M${x},${base}h${w}v-1h${-w}Z`;
    }
    const y = base - h;
    const r = Math.min(4, w / 2, h);
    return `M${x},${base}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${base}Z`;
  }

  onHover(i: number): void {
    const b = this.chart[i];
    this.hover = { x: ((this.barX(i) + this.barW / 2) / this.W) * 100, label: b.label, value: b.value };
  }
}
